/**
 * What a vendor supplied, line by line — TRBUD-418 (docs/plans/TRBUD-418.md).
 *
 * `GET /vendors/:id/supplied-lines` lists each part and labor charge in a date
 * range, year to date by default, from the money log (`v_report_money_log`),
 * which already leaves out drafts, voided lines and records-only files and nets
 * reversals. Eden's rulings, 22 October 2026, which the shapes below carry:
 *
 *   - As the biller (the default) the totals ARE the vendor's spend, so the
 *     default range equals Spend YTD.
 *   - As the shop that did the work, a line another company billed is shown on
 *     its own line and never added to this vendor's spend.
 *   - MaintainX history is one summary line per job and kind; MaintainX's own
 *     lines show under it as reference only, never added.
 *   - Export goes through the report builder (`POST /reports/matrix/export`):
 *     `suppliedLinesExportDefinition` builds the Money log definition. There is
 *     no CSV route of the panel's own.
 *
 * Browser-safe: zod, types and one pure function. All money is USD.
 */
import { z } from "zod";
import { isRealCalendarDay } from "./field-shapes.js";
import type { MatrixDefinition } from "./report-matrix.js";

/** `v_cost_line_spend.spend_class`, the panel's three tabs. */
export const SUPPLIED_LINE_CLASSES = ["parts", "labour", "other"] as const;
export type SuppliedLineClass = (typeof SUPPLIED_LINE_CLASSES)[number];
export const SUPPLIED_LINE_CLASS_LABEL: Record<SuppliedLineClass, string> = { parts: "Parts", labour: "Labor", other: "Other" };

/** Which side of a bill the vendor is read from. */
export const SUPPLIED_LINE_ROLES = ["biller", "shop"] as const;
export type SuppliedLineRole = (typeof SUPPLIED_LINE_ROLES)[number];

const fleetDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine(isRealCalendarDay, "expected a real calendar day");

/** `GET /vendors/:id/supplied-lines`. Dates are fleet days; both default to the Spend YTD window. */
export const vendorSuppliedLinesQuery = z
  .object({
    from: fleetDay.optional(),
    to: fleetDay.optional(),
    class: z.enum(SUPPLIED_LINE_CLASSES).optional(),
    as: z.enum(SUPPLIED_LINE_ROLES).default("biller"),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().min(0).max(1_000_000).default(0),
  })
  .strict()
  .superRefine((value, context) => {
    if (value.from !== undefined && value.to !== undefined && value.from > value.to) {
      context.addIssue({ code: "custom", path: ["from"], message: "The start of the range is after its end." });
    }
  });
export type VendorSuppliedLinesQuery = z.infer<typeof vendorSuppliedLinesQuery>;

/** A bill's file, opened in the page (never downloaded). */
export interface SuppliedLineFile {
  documentId: string;
  fileName: string;
  mimeType: string | null;
  /** The content route shows it in the page (`documentDelivery` is `inline`). */
  canPreview: boolean;
}

/** One of MaintainX's own lines under its summary: reference only, never added. */
export interface MainxReferenceLine {
  /** Null when there was none, or when it was held as personal data (`held`). */
  description: string | null;
  held: boolean;
  quantity: string | null;
  unitCost: string | null;
  amount: string;
}

export interface SuppliedLine {
  /** `v_report_money_log.row_id`: one MaintainX line can split into several rows. */
  rowId: string;
  costEntryId: string;
  /** The fleet day the line counts on. */
  costDate: string;
  workOrderId: string;
  /** `WO-…`, from `workOrderDisplayId`. */
  workOrderLabel: string;
  unitLabel: string | null;
  spendClass: SuppliedLineClass;
  costKind: string;
  source: "truckbuddy" | "maintainx";
  /** The part's name, else the line's own text; "MaintainX history" for a MaintainX summary. Null: none recorded. */
  description: string | null;
  descriptionSource: "part" | "line" | "maintainx" | "none";
  /** As stated on the line; null is "not stated". */
  quantity: string | null;
  unitOfMeasure: string | null;
  unitCost: string | null;
  amount: string;
  /** Vendor labor hours, only when the line labels them in hours. */
  labourHours: string | null;
  /** Labor with no labelled hours: a flat-rate charge. */
  flatRate: boolean;
  invoiceNumber: string | null;
  vendorInvoiceId: string | null;
  /** Null with no file, or when withheld (`fileWithheld`). */
  file: SuppliedLineFile | null;
  /** There is a file this viewer may not open: financial documents are restricted. */
  fileWithheld: boolean;
  /** As the shop: the company that billed this work, when it is not this vendor. Never added to its spend. */
  billedBy: { vendorId: string; name: string } | null;
  /** MaintainX's own lines under a MaintainX summary row. Empty otherwise. */
  mainxReference: MainxReferenceLine[];
}

export interface SuppliedLinesTotals {
  byClass: Record<SuppliedLineClass, string>;
  total: string;
  lines: number;
}

/** What `GET /vendors/:id/supplied-lines` answers. */
export interface VendorSuppliedLines {
  vendorId: string;
  vendorName: string;
  as: SuppliedLineRole;
  /** The fleet days counted; `yearToDate` when both were left to their defaults. */
  window: { from: string; to: string; yearToDate: boolean };
  class: SuppliedLineClass | null;
  items: SuppliedLine[];
  /** Rows under the whole filter, the class tab included. */
  totalRows: number;
  limit: number;
  offset: number;
  /**
   * Over the whole filter, every class, never the page. As the biller this is
   * the vendor's spend in the window; as the shop, its own bills only.
   */
  totals: SuppliedLinesTotals;
  /** As the shop: work it did on another company's bill. Shown apart, never added. Null as the biller. */
  billedByOthers: SuppliedLinesTotals | null;
  /** The bills behind the counted lines: what they say, beside what their lines in the window add to. */
  bills: { count: number; billsSay: string; linesAddTo: string };
  /** MaintainX expense on this vendor's jobs not yet split, in the window. Null when there is none. */
  unsplitMainx: { amount: string; workOrders: number } | null;
  currency: "USD";
}

/** The Money log columns the panel exports, in its order. Every id is a registry field. */
export const SUPPLIED_LINES_EXPORT_COLUMNS = [
  "money_log.cost_date", "money_log.work_order.number", "money_log.asset.unit_label", "money_log.spend_class",
  "money_log.cost_kind", "money_log.part.description", "money_log.line_description", "money_log.quantity",
  "money_log.unit_of_measure", "money_log.unit_cost", "money_log.amount", "money_log.vendor_labour_hours",
  "money_log.invoice_number", "money_log.line_vendor.name", "money_log.provider_vendor.name", "money_log.source",
] as const;

/**
 * The report-builder definition behind the panel's Export: the same vendor (as
 * biller or as shop), class and window, so its rows are the panel's rows.
 * Year to date exports as the 'ytd' timeframe; any other window as a range on
 * the cost date.
 */
export function suppliedLinesExportDefinition(
  vendorId: string,
  view: { as: SuppliedLineRole; class: SuppliedLineClass | null; window: { from: string; to: string; yearToDate: boolean } },
): MatrixDefinition {
  const filters: MatrixDefinition["filters"] = [
    { field: view.as === "shop" ? "money_log.provider_vendor.vendor_id" : "money_log.line_vendor.vendor_id", operator: "eq", value: vendorId },
  ];
  if (view.class !== null) filters.push({ field: "money_log.spend_class", operator: "eq", value: view.class });
  if (!view.window.yearToDate) filters.push({ field: "money_log.cost_date", operator: "range", from: view.window.from, to: view.window.to });
  return {
    version: 2,
    base: "money_log",
    mode: "detail",
    columns: SUPPLIED_LINES_EXPORT_COLUMNS.map((field) => ({ field })),
    filters,
    timeframe: view.window.yearToDate ? "ytd" : "all",
    shareMode: "full",
    sort: [{ column: 0, direction: "desc" }],
  };
}
